/**
 * The signed-in shell's navigation, shared by the desktop sidebar, the mobile
 * sheet, the header breadcrumb, the command palette and the Ask Ava panel's
 * "Viewing" badge, so one list names every section (plan 03 CHG-18).
 *
 * Sections and labels copy the PA (UK) demo screenshots: COMMAND CENTER,
 * AI INTELLIGENCE, PLATFORM, then ADMINISTRATION and SYSTEM folded away. Page
 * titles elsewhere keep the canonical nouns (Auto Workbench, Auto Policies,
 * Auto Insights); only the nav and breadcrumb use the PA labels (00-index,
 * "Sidebar labels").
 *
 * Visibility here is presentation only. Every page and endpoint still enforces
 * its own authz (Rule 12); hiding an item never grants or removes access.
 */

import type { ElementType } from 'react'
import { Phone } from 'lucide-react'
import { Icons } from '@/components/ui/icons'
import { breadcrumbTrail, type Crumb } from './breadcrumbs'

/**
 * Who the shell is drawn for, worked out from the session roles.
 *
 * - `standard`: `admin` or `user` (the recruiter), and sessions with no known
 *   role yet (pending approval). They keep the full nav; pages show their own
 *   approval state.
 * - `logistics_leader`: a leader without `user` keeps the Command Briefing and
 *   its Logistics view (D9).
 * - `panelist`: a panelist-only session sees no nav at all; the middleware
 *   keeps it on the "Panel reviews happen by email" page (Plan 09 C09-19,
 *   D09-12). Kept as an audience so that session never falls into `standard`.
 */
export type NavAudience = 'standard' | 'logistics_leader' | 'panelist'

export interface NavItem {
  href: string
  label: string
  icon: ElementType
  /** Audiences that see the item. Default: `standard` only. */
  audiences?: NavAudience[]
  /** When set, the session must also hold one of these roles. */
  roles?: string[]
}

export interface NavSection {
  title: string
  items: NavItem[]
  /**
   * Folded away until somebody asks for it (PA shell). Seven administration
   * entries sit on screen throughout a demo that never opens one of them.
   * Collapsing is not removing: the heading stays and one click opens it.
   */
  collapsedByDefault?: boolean
}

const ADMIN_ONLY = { roles: ['admin'] }

export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Command Center',
    items: [
      {
        href: '/',
        label: 'Command Briefing',
        icon: Icons.dashboard,
        audiences: ['standard', 'logistics_leader'],
      },
      // Plan 09 C09-19: no My panels item; panelists review by email (D09-11).
      { href: '/workbench', label: 'Workbench', icon: Icons.inbox },
    ],
  },
  {
    title: 'AI Intelligence',
    items: [
      { href: '/ai/policies', label: 'AI Policies', icon: Icons.brain },
      { href: '/ai/insights', label: 'AI Insights', icon: Icons.lightbulb },
      { href: '/agent-hub', label: 'Coworker Hub', icon: Icons.network },
    ],
  },
  {
    title: 'Platform',
    items: [
      { href: '/data-manager', label: 'Data Manager', icon: Icons.layers },
      // A presenter aid, kept so nothing is lost; admins only (plan 03 CHG-18).
      { href: '/voice-screening', label: 'Voice Screening', icon: Phone, ...ADMIN_ONLY },
    ],
  },
  {
    title: 'Administration',
    collapsedByDefault: true,
    items: [
      { href: '/admin/users', label: 'User Management', icon: Icons.users, ...ADMIN_ONLY },
      { href: '/admin/roles', label: 'Role Management', icon: Icons.shield, ...ADMIN_ONLY },
      { href: '/admin/groups', label: 'Group Management', icon: Icons.network, ...ADMIN_ONLY },
      { href: '/admin/sessions', label: 'User Sessions', icon: Icons.device, ...ADMIN_ONLY },
      { href: '/admin/events', label: 'Login Events', icon: Icons.activity, ...ADMIN_ONLY },
      { href: '/admin/audit', label: 'Audit Logs', icon: Icons.fileText, ...ADMIN_ONLY },
      { href: '/admin/settings', label: 'Admin Settings', icon: Icons.settings, ...ADMIN_ONLY },
    ],
  },
  {
    title: 'System',
    collapsedByDefault: true,
    items: [{ href: '/settings', label: 'Settings', icon: Icons.settings }],
  },
]

/** Works out which shell a session gets (see NavAudience). */
export function navAudience(roles: readonly string[] | undefined): NavAudience {
  const held = roles ?? []
  if (held.includes('admin') || held.includes('user')) return 'standard'
  if (held.includes('logistics_leader')) return 'logistics_leader'
  if (held.includes('panelist')) return 'panelist'
  return 'standard'
}

/** The sections and items a session sees, empty sections dropped. */
export function visibleNavSections(roles: readonly string[] | undefined): NavSection[] {
  const held = roles ?? []
  const audience = navAudience(held)
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (!(item.audiences ?? ['standard']).includes(audience)) return false
      if (item.roles && !item.roles.some((role) => held.includes(role))) return false
      return true
    }),
  })).filter((section) => section.items.length > 0)
}

/**
 * Prefix match, so nested routes keep their section lit: `/workbench/queues`
 * and `/workbench/drivers/<id>` highlight Workbench. `/` matches only itself.
 */
export function isNavItemActive(pathname: string | null | undefined, href: string): boolean {
  if (!pathname) return false
  if (href === '/') return pathname === '/'
  return pathname === href || pathname.startsWith(`${href}/`)
}

/**
 * Breadcrumb and "Viewing" labels for path segments whose label is a name
 * rather than a word. Every other segment is title-cased with hyphens turned
 * into spaces, which is right for `/workbench`, `/settings` and `/admin/*`.
 */
export const ROOT_LABEL = 'Command Briefing'

export const SEGMENT_LABELS: Record<string, string> = {
  ai: 'AI',
  'agent-hub': 'Coworker Hub',
  'auto-graph': 'Auto Graph',
  'data-manager': 'Data Manager',
  'voice-screening': 'Voice Screening',
  // Plan 09 C09-19: the page a panelist-only session lands on.
  'panel-email': 'Panel reviews',
}

export function segmentLabel(segment: string): string {
  return SEGMENT_LABELS[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
}

/**
 * Path prefixes with no page of their own (`/ai/policies` exists, `/ai` does not).
 * Their crumb is left out so the breadcrumb never links to a 404.
 */
export const PATHS_WITHOUT_PAGE: ReadonlySet<string> = new Set(['/ai', '/admin', '/workbench/drivers'])

/**
 * Breadcrumb trail for a path, always rooted at the Command Briefing. A record
 * page passes ``recordLabel`` (the driver's name) so the last crumb never shows
 * an id (sandbox QA defect 2); see ./breadcrumbs.ts.
 */
export function breadcrumbsFor(pathname: string | null | undefined, recordLabel?: string | null): Crumb[] {
  return breadcrumbTrail(pathname, { rootLabel: ROOT_LABEL, labelOf: segmentLabel, pathsWithoutPage: PATHS_WITHOUT_PAGE, recordLabel })
}

/** Brand block middle line; template default keeps the shell domain-free (CHG-19). */
export const AUTO_NAME = process.env.NEXT_PUBLIC_AUTO_NAME || 'Driver Recruiting'
